// 11 §10.6 Aftermath (P0 subset + the adventure settlement): the outcome stamped across the top,
// then for an adventure the `settlement` row (outcome row, XP and gold each), the Chronicle's
// `AdventureSettled` sentence and the loot (this instance's `loot_draw` rows and my heroes' items
// acquired since it started); for a lobby quick fight the encounter outcome and per-hero
// `encounter_result`; then the level-up offers as the shared `LevelUpCards` (no percentages
// anywhere in this copy — §10.4). Built from the component library (§10.10); the layout classes
// `fk-aftermath__*` live in adventure.css. A ranger's beast stands at their heel in every hero cell
// (docs/59 companions; a beast has no row of its own, CC6).
import { Badge, Button, Card, Display, Divider, Eyebrow, HeroSprite, OryxIcon, Panel, Portrait, Quote, Reveal, Stamp, Stat, Stats, Table, type Column } from '../components';
import { useRoute } from '../../core/stores/route';
import { useSession } from '../../core/stores/session';
import { useEncounterResults, useEncounterRow, useHeroCallings, useInstance, useInstanceChronicle, useLootDraws, useMyInventory, useMyLevelUpOffers, usePartyMembers, useSettlement, useWorldHeroes, useWorldItems } from '../../core/subscriptions/hooks';
import { leaveEncounter } from '../../core/encounter/session';
import { identityHex, tsToMs } from '../../core/net/connection';
import { CLASS_NAMES } from '../../core/sim/adapt';
import { outcomeLabel } from '../../core/adventure/graph';
import { LevelUpCards } from './LevelUp';
import { OUTCOMES } from './levelupData';
import { TIER_NAMES } from './World';
import { ChatDock } from './ChatDock';
import { usePlace } from '../../core/audio/places';
import { beastOf } from './Lobby';
import { ItemIcon } from '../inventory/ItemCard';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const tierTone = (tier: number) => (tier >= 3 ? 'gold' : tier === 2 ? 'green' : 'grey');

/** The stamp's word and tone for a settlement outcome row (0 Success … 4 Wipe). */
const SETTLE_STAMP: Array<{ word: string; tone: 'victory' | 'defeat' | 'neutral' }> = [
  { word: 'Success', tone: 'victory' },
  { word: 'Partial success', tone: 'victory' },
  { word: 'Soft fail', tone: 'neutral' },
  { word: 'Abandoned', tone: 'neutral' },
  { word: 'Wipe', tone: 'defeat' },
];
/** … and for an encounter outcome (OUTCOMES: Victory, Wipe, Fled, Surrendered, Stalemate, Abandoned, Failed). */
const ENC_TONE: Array<'victory' | 'defeat' | 'neutral'> = ['victory', 'defeat', 'neutral', 'neutral', 'neutral', 'neutral', 'defeat'];

export function AftermathScreen() {
  usePlace({ key: 'place.camp', layers: ['place.fire'] });   // docs/44: the camp after the fight, its fire
  const encounterId = useRoute((s) => s.encounterId);
  const instanceId = useRoute((s) => s.instanceId);
  const partyId = useRoute((s) => s.partyId);
  const go = useRoute((s) => s.go);
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const enc = useEncounterRow(encounterId);
  const results = useEncounterResults(encounterId);
  const heroes = useWorldHeroes(worldId);
  const callings = useHeroCallings(worldId);
  const offers = useMyLevelUpOffers().filter((o) => o.worldId === worldId);
  const inst = useInstance(instanceId);
  const settlement = useSettlement(instanceId);
  const draws = useLootDraws(instanceId);
  const worldItems = useWorldItems(settlement ? worldId : undefined);
  const myItems = useMyInventory();
  const chronicle = useInstanceChronicle(worldId, instanceId);
  const members = usePartyMembers(inst?.partyId);
  const heroById = new Map(heroes.map((h) => [h.id.toString(), h]));
  const outcome = enc?.outcome !== undefined && enc.outcome !== null ? OUTCOMES[enc.outcome] ?? `outcome ${enc.outcome}` : 'ended';
  const settled = chronicle.find((c) => c.kindName === 'AdventureSettled');
  const startedMs = tsToMs(inst?.startedAt) ?? 0;
  const loot = myItems.filter((i) => i.worldId === worldId && (tsToMs(i.acquiredAt) ?? 0) >= startedMs && inst !== undefined).sort((a, b) => (a.id < b.id ? -1 : 1));
  const deployed = [...members].filter((m) => m.deployed).sort((a, b) => (a.heroId < b.heroId ? -1 : 1));
  const adventure = !!settlement;
  const back = () => { leaveEncounter(); if (adventure) go('world', { encounterId: undefined, instanceId: undefined }); else go('lobby', { encounterId: undefined }); };

  // The headline stamp: the adventure's settlement when there is one, else the room's outcome.
  const stamp = adventure
    ? SETTLE_STAMP[settlement.outcomeRow] ?? { word: outcomeLabel(settlement.outcomeRow), tone: 'neutral' as const }
    : { word: outcome, tone: ENC_TONE[enc?.outcome ?? 4] ?? 'neutral' };

  const heroCell = (heroId: bigint, owner?: string) => {
    const h = heroById.get(heroId.toString());
    const mine = owner !== undefined ? owner === me : h?.owner.toHexString() === me;
    const beast = beastOf(callings, heroId);
    return (
      <span className="fk-aftermath__hero">
        <span className="fk-aftermath__face">
          <Portrait heroId={h?.id} species={h?.species} frame={h?.oryxFrame} classId={CLASS_NAMES[h?.class ?? 0]} scale={2} title={h?.name} />
          {beast && <span className="fk-aftermath__beast" data-testid="aftermath-beast" data-beast-name={beast.name}><HeroSprite frame={beast.frame} classId="companion" scale={2} title={beast.name === beast.kind ? beast.kind : `${beast.name} the ${beast.kind}`} /></span>}
        </span>
        <span>{h?.name ?? `hero ${heroId}`}{beast && <small className="fk-aftermath__beast-name"> &amp; {beast.name}</small>}</span>
        {mine && <Badge tone="gold">yours</Badge>}
      </span>
    );
  };

  type SettleRow = (typeof deployed)[number];
  const settleColumns: Array<Column<SettleRow>> = [
    { key: 'hero', head: 'Hero', cell: (m) => heroCell(m.heroId, identityHex(m.owner)) },
    { key: 'owner', head: 'Owner', cell: (m) => (identityHex(m.owner) === me ? 'you' : identityHex(m.owner).slice(-6)) },
    { key: 'xp', head: 'XP', num: true, cell: () => <span data-testid="settlement-xp">+{settlement?.xpEach ?? 0} xp</span> },
    { key: 'gold', head: 'Gold', num: true, cell: () => `+${settlement?.goldEach ?? 0} gold` },
  ];

  type ResultRow = (typeof results)[number];
  const resultColumns: Array<Column<ResultRow>> = [
    { key: 'hero', head: 'Hero', cell: (r) => heroCell(r.heroId) },
    { key: 'hp', head: 'HP', num: true, cell: (r) => <>{r.hpEnd}{r.downed ? <Badge tone="red">downed</Badge> : null}</> },
    { key: 'dealt', head: 'Dealt', num: true, cell: (r) => r.dealt },
    { key: 'taken', head: 'Taken', num: true, cell: (r) => r.taken },
    { key: 'xp', head: 'XP', num: true, cell: (r) => `+${r.xp}` },
    { key: 'gold', head: 'Gold', num: true, cell: (r) => `+${r.gold}` },
    { key: 'note', head: '', cell: (r) => (r.finalBlows ? <Badge tone="amber">{r.finalBlows} final blow{r.finalBlows === 1 ? '' : 's'}</Badge> : null) },
  ];
  const sortedResults = [...results].sort((a, b) => (a.heroId < b.heroId ? -1 : 1));

  return (
    <main className="fk-screen fk-screen--aftermath fk-hit" data-testid="aftermath" data-settled={adventure}>
      <div className="fk-aftermath__col">
        <div className="fk-aftermath__stamp">
          <Stamp tone={stamp.tone} sub={adventure && inst ? `${inst.title} · ${cap(inst.location)}` : enc ? `${enc.name} · ${enc.pulseIndex} turn${enc.pulseIndex === 1 ? '' : 's'}` : undefined}>{stamp.word}</Stamp>
        </div>

        {adventure && inst && (
          <Panel variant="ornate" label="Adventure settled" rise glow>
            <div className="fk-aftermath__headline">
              <div className="fk-aftermath__headline-text">
                <Eyebrow>Adventure settled</Eyebrow>
                <Display size="lg" gold data-testid="settlement-outcome">{inst.title} — {outcomeLabel(settlement.outcomeRow)}</Display>
              </div>
              <Badge tone={settlement.outcomeRow <= 1 ? 'green' : settlement.outcomeRow === 4 ? 'red' : 'amber'} filled>{outcomeLabel(settlement.outcomeRow)}</Badge>
            </div>
            {settled && <Quote data-testid="settlement-sentence">{settled.summary} <span className="fk-glyph" title="Chronicle" aria-label="Chronicle">◉</span></Quote>}
            <Stats className="fk-aftermath__stats">
              <Stat label="Sites" value={inst.visited.length} unit="visited" />
              <Stat label="Rooms" value={inst.cleared.length} unit="cleared" />
              <Stat label="Clues" value={inst.cluesFound.length} />
              <Stat label="Wipes" value={inst.wipes} delta={inst.wipes > 0 ? 'down' : undefined} />
              <Stat label="Encounters" value={inst.encounters.length} />
            </Stats>
            <Divider tight>The party's share</Divider>
            <Table className="fk-aftermath__table" label="Settlement" columns={settleColumns} rows={deployed} rowKey={(m) => m.id.toString()} rowTestId="settlement-row" empty="no deployed heroes recorded" />
            <Divider tight>Loot</Divider>
            {draws.length === 0 && loot.length === 0 && (
              <p className="fk-muted" data-testid="no-loot">No loot this time — only a full success draws from the table.</p>
            )}
            {(draws.length > 0 || loot.length > 0) && (
              <div className="fk-aftermath__loot" data-testid="loot-draws">
                {[...draws].sort((a, b) => (a.id < b.id ? -1 : 1)).map((d, k) => {
                  const it = worldItems.find((i) => i.id === d.itemId);
                  const h = heroById.get(d.heroId.toString());
                  return (
                    <Card key={`d${d.id}`} column index={k} className="fk-aftermath__loot-card" data-testid="loot-draw">
                      {it ? <ItemIcon def={it.def} tier={it.tier} name={it.name} relic={it.relic} scale={3} framed title={it.name} /> : <OryxIcon name="chest_gold_open" scale={3} framed title={d.table} />}
                      <span className="fk-aftermath__loot-name">{it?.name ?? `item #${d.itemId}`}</span>
                      <span className="fk-aftermath__loot-badges">
                        {it && <Badge tone={tierTone(it.tier)}>{TIER_NAMES[it.tier] ?? `tier ${it.tier}`}</Badge>}
                        {d.bumped && <Badge tone="amber">bumped a tier</Badge>}
                        {d.redrawn && <Badge tone="grey">redrawn</Badge>}
                      </span>
                      <span className="fk-muted">{h?.name ?? `hero ${d.heroId}`} drew it from {d.table}</span>
                    </Card>
                  );
                })}
                {loot.length > 0 && draws.length === 0 && loot.map((i, k) => (
                  <Card key={`l${i.id}`} column index={k} className="fk-aftermath__loot-card" data-testid="my-loot">
                    <ItemIcon def={i.def} tier={i.tier} name={i.name} relic={i.relic} scale={3} framed title={i.name} />
                    <span className="fk-aftermath__loot-name">{i.name}</span>
                    <span className="fk-aftermath__loot-badges">
                      <Badge tone={tierTone(i.tier)}>{TIER_NAMES[i.tier] ?? `tier ${i.tier}`}</Badge>
                      {i.relic && <Badge tone="plum">relic</Badge>}
                    </span>
                    <span className="fk-muted">{heroById.get(i.heroId.toString())?.name ?? 'a hero'} carries it</span>
                  </Card>
                ))}
              </div>
            )}
          </Panel>
        )}

        {(enc || !adventure) && (
          <Panel variant={adventure ? 'default' : 'ornate'} label="Encounter aftermath" rise title={adventure ? 'The last room' : undefined}>
            {!adventure && (
              <div className="fk-aftermath__headline">
                <div className="fk-aftermath__headline-text">
                  <Eyebrow>Aftermath</Eyebrow>
                  <Display size="lg" gold data-testid="outcome">{enc?.name ?? 'The encounter'} — {outcome}</Display>
                </div>
                {enc && <Badge tone="grey">{enc.pulseIndex} turn{enc.pulseIndex === 1 ? '' : 's'}</Badge>}
              </div>
            )}
            {adventure && <p className="fk-muted" data-testid="outcome">{enc?.name ?? 'The encounter'} — {outcome}{enc ? ` · ${enc.pulseIndex} turn${enc.pulseIndex === 1 ? '' : 's'}` : ''}</p>}
            <Table className="fk-aftermath__table" label="Encounter results" columns={resultColumns} rows={sortedResults} rowKey={(r) => r.id.toString()} rowTestId="result-row" empty="no results recorded" />
          </Panel>
        )}

        {offers.map((o) => {
          const h = heroById.get(o.heroId.toString());
          return (
            <Panel key={o.id.toString()} variant="ornate" label="Level up" rise glow title={`Level up — ${h?.name ?? 'a hero'} reaches level ${o.toLevel}`}>
              <LevelUpCards offer={o} hero={h} />
            </Panel>
          );
        })}

        {worldId && <ChatDock worldId={worldId} partyId={partyId} variant="panel" className="fk-chatdock--column" />}

        <Reveal className="fk-aftermath__back fk-hit">
          <Button primary size="lg" onClick={back} data-testid={adventure ? 'back-to-world' : 'back-to-lobby'}>
            {adventure ? 'Back to the World' : 'Back to the lobby'}
          </Button>
        </Reveal>
      </div>
    </main>
  );
}
